import { z } from 'zod';
import { recordListsViewSchema } from '../contracts/record-lists';
import { recordedActionTextSchema } from '../contracts/recorded-action';
import { actionDecisionContextSchema, actionDecisionRowSchema, actionDecisionViewSchema }
  from '../../decision-ledger-saved-action';

const text = z.string();
const effect = z.object({ label: text, display: text, basis: z.literal('modeled') }).strict();
/** "Saved plan" only when each chosen action has an owner, date, range and the gap it closes. */
export const SAVED_PLAN_TITLES = { saved: 'Saved plan', draft: 'Draft plan' } as const;
/** Display DTO only. Engine computation and saved-plan admission remain server-owned. */
export const savedActionPlanViewSchema = z.object({
  recordLists: recordListsViewSchema.optional(),
  state: z.enum(['selected', 'no_action', 'unreadable', 'ambiguous']),
  message: text.nullable(), savedAt: text.nullable(), source: text,
  summaryLine: text.optional(),
  /** Server-judged completeness; `missing` lists every absent field once, on one line. */
  status: z.object({ state: z.enum(['saved', 'draft']), missing: text.nullable() }).strict()
    .optional(),
  programEffects: z.array(effect).optional(),
  decisionContext: actionDecisionContextSchema.optional(),
  address: z.object({ dealId: text, asOf: text, viewId: z.literal('decisions'),
    params: z.record(text, text) }).strict(),
  groups: z.array(z.object({ kind: z.enum(['actions', 'find_out_first']),
    entries: z.array(z.object({ state: z.enum(['ranked', 'tie', 'unranked']),
      rows: z.array(z.object({ stepRef: text, action: text,
        catch: recordedActionTextSchema.optional(), firstCheckInstruction: recordedActionTextSchema.optional(),
        // null: not saved for this row; the absence is stated once in `status.missing`.
        owner: text.nullable(), due: text.nullable(), why: text.nullable(),
        firstCheck: text,
        firstCheckState: z.enum(['scheduled', 'not_yet_scheduled', 'unavailable']),
        decisionState: z.enum(['proposed', 'rejected']),
        decision: actionDecisionViewSchema.optional(),
        decisionAction: actionDecisionRowSchema.optional(),
        effectMessage: text.nullable().optional(),
        effects: z.array(effect) }).strict()) }).strict()) }).strict()),
}).strict();
export type SavedActionPlanView = z.infer<typeof savedActionPlanViewSchema>;

export function readSavedActionPlanAddition(value: unknown): { actionPlan?: SavedActionPlanView } | null {
  if (value === undefined) return {};
  const parsed = savedActionPlanViewSchema.safeParse(value);
  return parsed.success ? { actionPlan: parsed.data } : null;
}
